'use client';

import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import BlogCard from './BlogCard';
import { ArrowRight, Sparkles } from 'lucide-react';

interface BlogSectionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialPosts?: Record<string, any>[];
}

export default function BlogSection({ initialPosts }: BlogSectionProps) {
  const t = useTranslations('blog');
  const featuredPostsLive = useQuery(api.blogPosts.getFeaturedPosts, { limit: 3 });
  const featuredPosts = featuredPostsLive ?? initialPosts;

  // Don't render if no posts
  if (!featuredPosts || featuredPosts.length === 0) {
    return null;
  }

  return (
    <section id="blog" className="py-24 px-6 bg-background" aria-labelledby="blog-heading">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-slide-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">
              {t('badge')}
            </span>
          </div>
          <h2
            id="blog-heading"
            className="mb-4 font-display text-[56px] font-normal leading-[114%] tracking-[0] text-[#221E1C]"
          >
            {t('title')}
            <span className="text-primary">{t('titleHighlight')}</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('subtitle')}
          </p>
        </div>

        {/* Blog Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {featuredPosts.map((post, index) => (
            <div
              key={post._id}
              className={`animate-fade-slide-up ${['[animation-delay:0ms]', '[animation-delay:100ms]', '[animation-delay:200ms]'][index] || ''}`}
            >
              <BlogCard
                title={post.title}
                titleZh={post.titleZh}
                slug={post.slug}
                excerpt={post.excerpt}
                excerptZh={post.excerptZh}
                featuredImageUrl={post.r2FeaturedImageUrl || post.featuredImageUrl}
                featuredImageAlt={post.featuredImageAlt}
                categoryName={post.category?.name || post.categorySlug}
                categorySlug={post.categorySlug}
                categoryColor={post.category?.color}
                readingTimeMinutes={post.readingTimeMinutes}
                publishedAt={post.publishedAt}
              />
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center animate-fade-slide-up [animation-delay:300ms]">
          <Link
            href="/blog"
            className="inline-flex items-center gap-2 px-8 py-4 bg-card text-foreground rounded-lg font-semibold text-lg border-2 border-border hover:border-primary/30 transition-all duration-300 hover:shadow-lg group"
          >
            <span>{t('viewAll')}</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}
